import { ref } from 'vue';
import { AIChatApi } from '@/api/ai-chat';
import { processMsgTextArray, extractAndMergeContent } from './message-content-format';

export type AudioAutoPlayMessage = {
    messageId?: string;
    botId?: string;
    messageTextContent?: string;
    botName?: string;
    isIntro?: boolean;
};

type TaskQueue = AudioAutoPlayMessage & {
    fetchAudioBase64Promise?: Promise<string | undefined>;
};

type AudioEventCallback = (message?: AudioAutoPlayMessage) => Promise<void> | void;

export enum AudioStatus {
    Loading = 'loading',
    Playing = 'playing',
    Idle = 'idle',
}

function MessageAudioAutoPlayController() {
    /**
     * @zh 音频实例 id
     */
    const AudioInstanceId = 'AudioAutoPlayInstanceID';
    /**
     * @zh 用于预加载音频
     */
    const TaskQueueLength = 2;
    /**
     * @zh 用于缓存音频 base64
     */
    const CacheAudioBase64MapLength = 10;
    /**
     * @zh 播放列表
     */
    let audioMessageList: AudioAutoPlayMessage[] = [];
    /**
     * @zh 音频实例
     */
    let audioInstance: HTMLAudioElement | null = null;
    /**
     * @zh 当前播放消息
     */
    const currentPlayingMessage = ref<AudioAutoPlayMessage | null>(null);
    /**
     * @zh 任务队列
     */
    let taskQueue: TaskQueue[] = [];
    let callbackMap:
        | Record<
              'onPlayLoadingCallback' | 'onPLayCallback' | 'onPlayEndCallback' | 'onPlayErrorCallback' | 'onPlayPauseCallback',
              AudioEventCallback | undefined
          >
        | undefined;
    /**
     * @zh Controller 实例状态
     */
    const status = ref<AudioStatus>(AudioStatus.Idle);
    /**
     * @zh Array 简单缓存 base64
     */
    const cacheAudioBase64Map: Array<{ key: string; value: string }> = [];
    /**
     * @zh 播放过的音频总条数
     */
    const playTotalCount = ref<number>(0);

    async function fetchAudioBase64(message?: AudioAutoPlayMessage) {
        if (!message) {
            return;
        }
        const cacheBase64 = cacheAudioBase64Map.find(item => item.key === message.messageId);

        if (cacheBase64) {
            return cacheBase64.value;
        }

        const res = await AIChatApi.aiChatTextAudio({
            botId: message.botId,
            content: processMsgTextArray(extractAndMergeContent(message.messageTextContent || '', message.botName || '')),
            isFirstTxt: message.isIntro,
        });
        if (cacheAudioBase64Map.length > CacheAudioBase64MapLength) {
            cacheAudioBase64Map.shift();
        }
        cacheAudioBase64Map.push({
            key: message.messageId || '',
            value: res?.data?.audio_base64 || '',
        });
        return res?.data?.audio_base64;
    }

    async function emitPlayTask(params?: { task?: TaskQueue }) {
        playTotalCount.value += 1;
        const { task } = params || {};
        const { onPlayLoadingCallback, onPLayCallback, onPlayEndCallback, onPlayErrorCallback, onPlayPauseCallback } = callbackMap || {};
        const currentMessage = {
            messageId: task?.messageId,
            botId: task?.botId,
            messageTextContent: task?.messageTextContent,
            isIntro: task?.isIntro,
        };

        if (!audioInstance) {
            const audioDom = document.getElementById(AudioInstanceId) as HTMLAudioElement | null;
            if (audioDom) {
                audioInstance = audioDom;
            } else {
                audioInstance = document.createElement('audio');
                audioInstance.id = AudioInstanceId;
                document.body.appendChild(audioInstance);
            }
        }

        currentPlayingMessage.value = currentMessage;
        status.value = AudioStatus.Loading;
        await onPlayLoadingCallback?.(currentMessage);
        const audioBase64 = await task?.fetchAudioBase64Promise;

        if (!audioBase64) {
            status.value = AudioStatus.Idle;
            // eslint-disable-next-line no-use-before-define
            onDestroy?.();
            await onPlayErrorCallback?.(currentMessage);
            return;
        }

        // audioInstance.oncanplaythrough = async () => {
        //     await audioInstance?.play?.();
        // };

        audioInstance.onplay = async () => {
            status.value = AudioStatus.Playing;
            await onPLayCallback?.(currentMessage);
        };

        audioInstance.onerror = async () => {
            status.value = AudioStatus.Idle;
            // eslint-disable-next-line no-use-before-define
            onDestroy?.();
            await onPlayErrorCallback?.(currentMessage);
        };

        audioInstance.onended = async () => {
            status.value = AudioStatus.Idle;
            await onPlayEndCallback?.(currentMessage);
            // eslint-disable-next-line no-use-before-define
            emit();
        };

        audioInstance.onpause = async () => {
            status.value = AudioStatus.Idle;
            await onPlayPauseCallback?.(currentMessage);
        };

        audioInstance.src = `data:audio/mp3;base64,${audioBase64}`;
        audioInstance?.play?.();
    }

    function onTaskQueueChange() {
        if (!currentPlayingMessage.value) {
            return;
        }
        const currentMessageIndex = audioMessageList.findIndex(item => item.messageId === currentPlayingMessage.value?.messageId);
        const preloadTaskMessage = audioMessageList[currentMessageIndex + 1];
        if (!preloadTaskMessage) {
            return;
        }
        taskQueue.push({
            ...preloadTaskMessage,
            fetchAudioBase64Promise: fetchAudioBase64(preloadTaskMessage),
        });
    }

    function onAudioMessageListChange(list?: AudioAutoPlayMessage[]) {
        /**
         * @zh 重置与销毁
         */
        // eslint-disable-next-line no-use-before-define
        onDestroy?.();

        audioMessageList = (list || []).filter(item => !!item.messageId && !!item.botId && !!item.messageTextContent);
        taskQueue = (audioMessageList || []).slice(0, TaskQueueLength).map(item => {
            return {
                ...item,
                fetchAudioBase64Promise: fetchAudioBase64(item),
            };
        });
    }

    async function emit(params?: {
        onPlayLoadingCallback?: AudioEventCallback;
        onPLayCallback?: AudioEventCallback;
        onPlayEndCallback?: AudioEventCallback;
        onPlayErrorCallback?: AudioEventCallback;
        onPlayPauseCallback?: AudioEventCallback;
    }) {
        if (taskQueue.length === 0) {
            // eslint-disable-next-line no-use-before-define
            onDestroy?.();
            return;
        }
        callbackMap = {
            onPlayLoadingCallback: params?.onPlayLoadingCallback,
            onPLayCallback: params?.onPLayCallback,
            onPlayEndCallback: params?.onPlayEndCallback,
            onPlayErrorCallback: params?.onPlayErrorCallback,
            onPlayPauseCallback: params?.onPlayPauseCallback,
        };
        const task = taskQueue.shift();
        if (!task) {
            return;
        }
        await emitPlayTask({ task });
        onTaskQueueChange();
    }

    function onDestroy() {
        // audioInstance?.remove?.();
        // audioInstance = null;
        audioInstance?.pause?.();
        currentPlayingMessage.value = null;
        status.value = AudioStatus.Idle;
        audioMessageList = [];
        taskQueue = [];
        callbackMap = undefined;
    }

    function onPause() {
        status.value = AudioStatus.Idle;
        audioInstance?.pause?.();
    }

    function onPlay() {
        status.value = AudioStatus.Playing;
        audioInstance?.play?.();
    }

    return {
        status,
        currentPlayingMessage,
        playTotalCount,
        emit,
        onAudioMessageListChange,
        onDestroy,
        onPause,
        onPlay,
    };
}

export const MessageAudioAutoPlayerControllerInstance = MessageAudioAutoPlayController();
